import { CircleHelp } from "lucide-react";
import type { ReactNode } from "react";

import type { DownPaymentComparisonRender } from "../../../lib/agent-types";
import {
  formatCurrencyFromCents,
  formatPercent,
} from "../../../lib/financials";
import { WidgetFrame, WidgetPlaceholderRow } from "../widget-frame";
import { Badge } from "../../ui/badge";
import { Tooltip } from "../../ui/tooltip";
import {
  hasMinDownIneligibleFlag,
  PRODUCT_MINIMUM_NOT_MET_LABEL,
} from "./review-flags";

export function DownPaymentComparisonWidget({
  payload,
}: {
  payload: DownPaymentComparisonRender;
}) {
  const comparison = payload.comparison;

  if (!comparison || comparison.columns.length === 0) {
    return <LegacyFallback payload={payload} />;
  }

  const summaryMarkers = getSummaryMarkers(comparison);
  const columnLabels = comparison.columns.map((column) => column.label);
  const hasProductFloorViolations = comparison.columns.some((column) =>
    hasMinDownIneligibleFlag(column.reviewFlags),
  );

  return (
    <WidgetFrame
      data-widget-type="down_payment_comparison"
      footer={
        <div className="rounded-2xl bg-muted/35 px-3 py-2">
          <div className="flex flex-wrap gap-1.5">
            {summaryMarkers.map((marker) => (
              <Badge
                key={marker.label}
                className="min-h-5 px-2 py-0.5 text-[9px] tracking-[0.1em]"
                variant={marker.variant}
              >
                {marker.label}
              </Badge>
            ))}
          </div>
          <p className="mt-2 text-sm leading-6 text-foreground">
            {comparison.summary.recommendedAction}
          </p>
          {hasProductFloorViolations ? (
            <p className="mt-2 text-xs leading-5 text-muted-foreground">
              Marked lanes need a higher down payment for the selected product.
            </p>
          ) : null}
        </div>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[460px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-card text-left text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground" />
              {comparison.columns.map((column) => (
                <th
                  key={column.label}
                  className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                >
                  <span className="block">{column.label}</span>
                  {hasMinDownIneligibleFlag(column.reviewFlags) ? (
                    <span className="mt-1 inline-flex rounded-pill border border-warning/25 bg-warning/10 px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-warning">
                      {PRODUCT_MINIMUM_NOT_MET_LABEL}
                    </span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <MoneyRow
              columnLabels={columnLabels}
              label="Cash to close"
              values={comparison.columns.map((column) => column.cashToClose)}
            />
            <MoneyRow
              columnLabels={columnLabels}
              label="Monthly payment"
              values={comparison.columns.map((column) => column.monthlyPayment)}
            />
            <TextRow
              columnLabels={columnLabels}
              label="PMI"
              values={comparison.columns.map((column) =>
                column.pmiMonthly > 0
                  ? formatCurrencyFromCents(column.pmiMonthly)
                  : "None",
              )}
            />
            <TextRow
              columnLabels={columnLabels}
              label="PMI dropoff"
              values={comparison.columns.map((column) =>
                column.pmiDropoffMonths
                  ? `~${column.pmiDropoffMonths} mo`
                  : "None",
              )}
            />
            <MoneyRow
              columnLabels={columnLabels}
              label="Cash left"
              values={comparison.columns.map(
                (column) => column.retainedLiquidity,
              )}
              emphasize
              valueTone={comparison.columns.map((column) =>
                getLiquidityTone(column.retainedLiquidity),
              )}
            />
            <TextRow
              columnLabels={columnLabels}
              label="Rate"
              values={comparison.columns.map((column) =>
                formatPercent(column.effectiveRate * 100, 3),
              )}
            />
            <TextRow
              columnLabels={columnLabels}
              label="Pricing adj."
              labelAccessory={<PricingAdjustmentTooltip />}
              values={comparison.columns.map((column) =>
                formatPoints(column.llpaPoints),
              )}
            />
          </tbody>
        </table>
      </div>
    </WidgetFrame>
  );
}

function MoneyRow({
  label,
  columnLabels,
  values,
  emphasize = false,
  valueTone,
}: {
  label: string;
  columnLabels: string[];
  values: number[];
  emphasize?: boolean;
  valueTone?: Array<"danger" | "success" | "warning">;
}) {
  return (
    <tr>
      <th
        scope="row"
        className={`sticky left-0 bg-card py-2 pr-3 text-left ${
          emphasize
            ? "text-sm font-semibold text-foreground"
            : "text-sm text-muted-foreground"
        }`}
      >
        {label}
      </th>
      {values.map((value, index) => (
        <td
          key={columnLabels[index]}
          className={`px-3 py-2 text-right font-bold tabular-nums ${
            emphasize ? "font-semibold text-foreground" : "text-foreground"
          }`}
        >
          {emphasize ? (
            <Badge
              variant={
                valueTone?.[index] === "danger"
                  ? "danger"
                  : valueTone?.[index] === "warning"
                    ? "warning"
                    : "success"
              }
            >
              {formatSignedCurrencyFromCents(value)}
            </Badge>
          ) : (
            formatCurrencyFromCents(value)
          )}
        </td>
      ))}
    </tr>
  );
}

function TextRow({
  label,
  labelAccessory,
  columnLabels,
  values,
}: {
  label: string;
  labelAccessory?: ReactNode;
  columnLabels: string[];
  values: string[];
}) {
  return (
    <tr>
      <th
        scope="row"
        className="sticky left-0 bg-card py-2 pr-3 text-left text-sm text-muted-foreground"
      >
        <span className="inline-flex items-center gap-1.5">
          <span>{label}</span>
          {labelAccessory}
        </span>
      </th>
      {values.map((value, index) => (
        <td
          key={columnLabels[index]}
          className="px-3 py-2 text-right text-foreground"
        >
          {value}
        </td>
      ))}
    </tr>
  );
}

function getLiquidityTone(value: number) {
  if (value < 0) {
    return "danger";
  }
  if (value < 5_000_000) {
    return "warning";
  }
  return "success";
}

function formatSignedCurrencyFromCents(value: number) {
  if (value < 0) {
    return `-${formatCurrencyFromCents(Math.abs(value))}`;
  }
  return formatCurrencyFromCents(value);
}

function formatPoints(value: number | null | undefined) {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "—";
  }

  return `${trimTrailingZeros((value * 100).toFixed(2))} pts`;
}

function trimTrailingZeros(value: string) {
  return value.replace(/\.?0+$/, "");
}

function getSummaryMarkers(
  comparison: NonNullable<DownPaymentComparisonRender["comparison"]>,
) {
  const markers: Array<{ label: string; variant: "primary" | "success" }> = [];

  if (comparison.summary.highestLiquidityLabel) {
    markers.push({
      label: `Most cash left: ${comparison.summary.highestLiquidityLabel}`,
      variant: "success",
    });
  }

  if (comparison.summary.lowestPaymentLabel) {
    markers.push({
      label: `Lowest payment: ${comparison.summary.lowestPaymentLabel}`,
      variant: "primary",
    });
  }

  if (comparison.summary.firstNoPMILabel) {
    markers.push({
      label: `No PMI: ${comparison.summary.firstNoPMILabel}`,
      variant: "success",
    });
  }

  return markers;
}

function PricingAdjustmentTooltip() {
  return (
    <Tooltip
      content={
        <div className="max-w-72 leading-5">
          Pricing adjustment is applied on top of the market base rate for each
          scenario. More down can reduce that adjustment, which is why different
          down-payment lanes may price differently.
        </div>
      }
    >
      <button
        type="button"
        aria-label="About down payment pricing adjustment"
        className="rounded-full text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      >
        <CircleHelp className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function LegacyFallback({ payload }: { payload: DownPaymentComparisonRender }) {
  return (
    <WidgetFrame data-widget-type="down_payment_comparison">
      <WidgetPlaceholderRow
        label="Target price"
        value={formatCurrencyFromCents(
          payload.estimate.request.property.target_price,
        )}
      />
      <WidgetPlaceholderRow
        label="Current down payment"
        value={formatCurrencyFromCents(
          payload.estimate.request.financing.down_payment_amount,
        )}
      />
    </WidgetFrame>
  );
}
